import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  getRequiredLegalDocuments,
  recordLegalAcceptance,
  LegalDocument,
} from "../../services/legalService";
import {
  ShieldAlert,
  CheckCircle2,
  Loader2,
  FileText,
  ChevronRight,
} from "lucide-react";
import { toast } from "react-toastify";
import { ROLES } from "../../constants/roles";

export const LegalAcceptanceModal: React.FC = () => {
  const { user } = useAuth();
  const [requiredDocs, setRequiredDocs] = useState<LegalDocument[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check required legal documents whenever user logs in or is validated
  const checkRequiredDocuments = useCallback(async () => {
    if (!user || user.isValidated !== true || user.role === ROLES.SUPERADMIN) {
      setRequiredDocs([]);
      setIsChecking(false);
      return;
    }

    try {
      setIsChecking(true);
      const res = await getRequiredLegalDocuments();
      if (res.requiresAcceptance && res.requiredDocuments?.length > 0) {
        setRequiredDocs(res.requiredDocuments);
        setCurrentIndex(0);
        setIsChecked(false);
      } else {
        setRequiredDocs([]);
      }
    } catch (err) {
      console.error("Error checking required legal documents:", err);
    } finally {
      setIsChecking(false);
    }
  }, [user]);

  useEffect(() => {
    checkRequiredDocuments();
  }, [checkRequiredDocuments]);

  // If no required documents or still checking, don't render modal
  if (isChecking || requiredDocs.length === 0) {
    return null;
  }

  const currentDoc = requiredDocs[currentIndex];
  if (!currentDoc) {
    return null;
  }

  const isLastDoc = currentIndex === requiredDocs.length - 1;

  const handleAccept = async () => {
    if (!isChecked || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await recordLegalAcceptance({
        legalDocumentId: currentDoc.id,
        documentType: currentDoc.documentType,
        version: currentDoc.version,
        action: "ACCEPTED",
      });

      toast.success(`${currentDoc.title} accepted successfully.`);

      if (isLastDoc) {
        // All required documents accepted!
        setRequiredDocs([]);
        toast.info("All required legal documents completed. Welcome!");
      } else {
        // Move to next document
        setCurrentIndex((prev) => prev + 1);
        setIsChecked(false);
      }
    } catch (err: any) {
      console.error("Failed to record legal acceptance:", err);
      toast.error(
        err.response?.data?.message || "Failed to record legal acceptance. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderLegalContent = (rawContent: string) => {
    if (!rawContent) return null;

    // Clean empty headers/paragraphs at the start (e.g. <h1><br></h1>)
    const cleaned = rawContent
      .replace(/^(\s*<h[1-6]>\s*(<br\s*\/?>)?\s*<\/h[1-6]>)+/i, "")
      .replace(/^(\s*<p>\s*(<br\s*\/?>)?\s*<\/p>)+/i, "");

    const hasHtml = /<[a-z][\s\S]*>/i.test(cleaned);

    if (hasHtml) {
      return (
        <div
          className="legal-content-body text-neutral-800 text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: cleaned }}
        />
      );
    }

    return (
      <div className="text-neutral-800 text-sm leading-relaxed whitespace-pre-wrap font-sans">
        {cleaned}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-neutral-900/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full flex flex-col max-h-[90vh] overflow-hidden border border-neutral-200 animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50/80 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <div>
              <h2 id="legal-modal-title" className="text-lg font-bold text-neutral-900">
                Action Required: Legal Policy Acceptance
              </h2>
              <p className="text-xs text-neutral-500">
                Document {currentIndex + 1} of {requiredDocs.length} requires your review and acceptance to continue.
              </p>
            </div>
          </div>

          <div className="hidden sm:flex items-center space-x-1.5 text-xs text-neutral-500">
            <span className="font-semibold text-primary-600">Step {currentIndex + 1}</span>
            <span>of {requiredDocs.length}</span>
          </div>
        </div>

        {/* Document Metadata Banner */}
        <div className="px-6 py-3 bg-neutral-100/60 border-b border-neutral-200 flex items-center justify-between gap-2 text-xs text-neutral-600">
          <div className="flex items-center space-x-2 font-medium text-neutral-900">
            <FileText className="h-4 w-4 text-primary-600" />
            <span>{currentDoc.title}</span>
          </div>
        </div>

        {/* Scrollable Document Text Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-white max-h-[50vh] sm:max-h-[55vh]">
          <div className="max-w-none">
            {renderLegalContent(currentDoc.content)}
          </div>
        </div>

        {/* Footer & Acceptance Checkbox */}
        <div className="p-6 border-t border-neutral-200 bg-neutral-50/60 space-y-4">
          <label className="flex items-start space-x-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
            />
            <span className="text-xs sm:text-sm text-neutral-700">
              I have read, understood, and agree to the{" "}
              <span className="font-semibold text-neutral-900">{currentDoc.title}</span>.
            </span>
          </label>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
            <p className="text-xs text-neutral-400 order-2 sm:order-1">
              Your acceptance will be securely recorded with a cryptographic server-side timestamp.
            </p>

            <button
              onClick={handleAccept}
              disabled={!isChecked || isSubmitting}
              className="btn-primary text-sm w-full sm:w-auto px-6 py-2.5 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Recording Acceptance...</span>
                </>
              ) : isLastDoc ? (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Accept &amp; Complete</span>
                </>
              ) : (
                <>
                  <span>Accept &amp; Next</span>
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalAcceptanceModal;
